import {
  useAccessibleBusinesses,
  useCurrentBusiness,
  useCurrentBusinessOutlets,
  useCurrentOutlet,
  useCurrentUser,
} from '@billx/api';
import { getAddressString } from '@billx/utils';
import { Alert, Box, CardContent, Stack, Typography } from '@mui/material';
import { MapPinIcon, UtensilsIcon } from 'lucide-react';
import React, { useMemo } from 'react';
import { use100vh } from 'react-div-100vh';

import { Loading } from '@/components/common';

interface InterstitialPageProps {
  children: React.ReactNode;
  selectOutlet?: boolean;
}

export const InterstitialGuard: React.FC<InterstitialPageProps> = ({
  children,
  selectOutlet = false,
}) => {
  const height = use100vh();
  const { updateUserPreference, isLoading: isUserLoading } = useCurrentUser();
  const { currentBusiness, isLoading: isBusinessLoading } =
    useCurrentBusiness();
  const { currentOutlet, isLoading: isOutletLoading } = useCurrentOutlet();

  const { accessibleBusinesses, isLoading: isBusinessesLoading } =
    useAccessibleBusinesses();

  const { outlets, isLoading: isOutletsLoading } = useCurrentBusinessOutlets();

  // Validate if selectedLocationId is valid under the selected business
  const isValidLocation = useMemo(() => {
    if (!selectOutlet || !currentBusiness?.id || !currentOutlet?.id) {
      return true; // No validation needed if not selecting outlet or no selection made
    }

    // Check if the selected outlet belongs to the selected business
    const isValid = outlets.some(
      (outlet) =>
        outlet.id === currentOutlet?.id &&
        outlet.businessId === currentBusiness?.id
    );

    return isValid;
  }, [selectOutlet, currentBusiness?.id, currentOutlet?.id, outlets]);

  const isLoading =
    isUserLoading ||
    isBusinessLoading ||
    isOutletLoading ||
    isBusinessesLoading ||
    isOutletsLoading;

  // Handle business selection
  const handleBusinessSelect = async (businessId: string) => {
    try {
      await updateUserPreference.mutateAsync({
        selectedBusinessId: businessId,
      });
    } catch (error) {
      console.error('Error updating business preference:', error);
    }
  };

  // Handle outlet selection
  const handleOutletSelect = async (outletId: string) => {
    try {
      await updateUserPreference.mutateAsync({
        selectedOutletId: outletId,
      });
    } catch (error) {
      console.error('Error updating outlet preference:', error);
    }
  };

  if (isLoading) {
    return <Loading />;
  }

  // Show business selector if no currentBusiness
  if (!currentBusiness) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height,
          px: 3,
        }}
      >
        <Box sx={{ maxWidth: 600, width: '100%' }}>
          <Typography variant="h4" gutterBottom textAlign="center">
            Select Business
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            textAlign="center"
            sx={{ mb: 4 }}
          >
            Choose the business you want to work with
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {accessibleBusinesses.map((business) => (
              <Box
                key={business.id}
                onClick={() => handleBusinessSelect(business.id)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                    borderColor: 'primary.light',
                  },
                  transition: 'all 0.2s ease-in-out',
                  gap: 2,
                }}
              >
                <Box color="primary.main">
                  <UtensilsIcon size={22} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6">{business.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {business.description}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    );
  }

  // Show outlet selector if currentBusiness exists and selectOutlet is true but no/invalid currentOutlet
  if (selectOutlet && (!currentOutlet || !isValidLocation)) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height,
          px: 3,
        }}
      >
        <Box sx={{ maxWidth: 600, width: '100%' }}>
          <Typography variant="h4" gutterBottom textAlign="center">
            Select Outlet
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            textAlign="center"
            sx={{ mb: 4 }}
          >
            Choose the outlet you want to work with
          </Typography>

          {/* Show warning if outlet is invalid */}
          {currentOutlet && !isValidLocation && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="body2">
                The previously selected outlet is no longer valid for the
                current business. Please select a new outlet.
              </Typography>
            </Alert>
          )}

          {/* Selected Business Card */}
          <Box
            sx={{
              mb: 3,
              border: '2px solid',
              borderColor: 'primary.main',
              borderRadius: 1,
            }}
          >
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <Box color="primary.main">
                  <UtensilsIcon size={22} />
                </Box>
                <Box>
                  <Typography variant="h6" color="primary">
                    {currentBusiness.name}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Box>

          {/* Location Selection */}
          <Box>
            <Typography variant="h6" gutterBottom>
              Available Outlets
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {outlets.map((outlet) => (
                <Box
                  key={outlet.id}
                  onClick={() => handleOutletSelect(outlet.id)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                      borderColor: 'primary.light',
                    },
                    transition: 'all 0.2s ease-in-out',
                    gap: 2,
                  }}
                >
                  <Box color="primary.main">
                    <MapPinIcon size={24} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6">{outlet.name}</Typography>
                    <Stack direction="row" spacing={1}>
                      <Typography variant="caption" color="text.secondary">
                        {getAddressString(outlet.address)}
                      </Typography>
                    </Stack>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  // Show children if all conditions are met
  return <>{children}</>;
};
